import React from 'react';
import AleaTable from '../../components/AleaTable/AleaTable';
import { ITableColumn } from '../../interfaces';
import { Box, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { ICaregiver } from '../../services/CaregiverService';

type CaregiverRow = ICaregiver & { actions?: unknown; carehome_id?: string };

interface CaregiversTableProps {
  caregivers: ICaregiver[];
  isLoading: boolean;
  total: number;
  showCareHomeColumn: boolean;
  canViewPin: boolean;
  hiddenPinIds: Set<string>;
  togglePinVisibility: (id: string) => void;
  onEditCaregiver?: (caregiver: ICaregiver) => void;
  onArchiveCaregiver?: (caregiver: ICaregiver) => void;
  onReactivateCaregiver?: (caregiver: ICaregiver) => void;
  isArchivedView: boolean;
  canManageCaregivers: boolean;
  activeActionRowId: string | null;
  setActiveActionRowId: (id: string | null) => void;
  usePagination?: boolean;
  currentPage?: number;
  perPage?: number;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
}

const CaregiversTable: React.FC<CaregiversTableProps> = ({
  caregivers,
  isLoading,
  total,
  showCareHomeColumn,
  canViewPin,
  hiddenPinIds,
  togglePinVisibility,
  onEditCaregiver,
  onArchiveCaregiver,
  onReactivateCaregiver,
  isArchivedView,
  canManageCaregivers,
  activeActionRowId,
  setActiveActionRowId,
  usePagination = false,
  currentPage = 1,
  perPage = 10,
  onPageChange,
  onPerPageChange,
}) => {
  const { t } = useTranslation();

  const getColumns = (): ITableColumn<CaregiverRow>[] => {
    const cols: ITableColumn<CaregiverRow>[] = [
    {
      key: 'name',
      label: t('name'),
      sortable: true,
      render: (data: ICaregiver) => <Text>{data.name || '-'}</Text>,
    },
    {
      key: 'pin',
      label: t('pinCareDocSys'),
      sortable: false,
        render: (data: ICaregiver) => {
          const hasPin = !!data.pin;
          const isVisible = canViewPin && !hiddenPinIds.has(data.id);

          return (
            <Flex w="100%" justifyContent="space-between" alignItems="center" pr="16px">
              <Text w="100%" textAlign="left">
                {hasPin ? (isVisible ? data.pin : '******') : '-'}
              </Text>
              {canViewPin && hasPin && (
                <Box
                  as="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    togglePinVisibility(data.id);
                  }}
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  color="alea.500"
                >
                  {isVisible ? (
                    <ViewOffIcon color="#2B4956" boxSize="20px" w="20px" h="20px" />
                  ) : (
                    <ViewIcon color="#C6C6C6" boxSize="20px" w="20px" h="20px" />
                  )}
                </Box>
              )}
            </Flex>
          );
        },
    },
  ];

    if (showCareHomeColumn) {
      cols.splice(1, 0, {
        key: 'carehome_id' as keyof CaregiverRow,
        label: t('careHome'),
        sortable: true,
        render: (data: ICaregiver) => <Text>{data.carehome?.name || '-'}</Text>,
      });
    }

    if (canManageCaregivers) {
      cols.push({
        key: 'actions' as keyof CaregiverRow,
        label: '',
        sortable: false,
        width: '35px',
        headerPadding: '0 0 0 10px',
        cellPadding: '0 0 0 10px',
        render: (data: ICaregiver) => {
          const rowIndex = caregivers.findIndex((c) => c.id === data.id);
          const isStriped = rowIndex % 2 === 1;
          const baseBg = isStriped ? 'white' : '#F3F2F2';
          const isActive = activeActionRowId === data.id;
          const menuBg = isActive ? 'alea.500' : baseBg;
          const iconColor = isActive ? 'white' : '#6E6E6E';

          return (
            <Flex justifyContent="flex-end">
              <Menu
                isOpen={activeActionRowId === data.id}
                onOpen={() => setActiveActionRowId(data.id)}
                onClose={() => setActiveActionRowId(null)}
                placement="bottom-end"
                autoSelect={false}
              >
                <MenuButton
                  as={Box}
                  w="25px"
                  h="50px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  cursor="pointer"
                  bg={menuBg}
                >
                  <IconButton
                    aria-label="more"
                    variant="ghost"
                    color={iconColor}
                    size="xs"
                    w="25px"
                    h="50px"
                    cursor="pointer"
                    _hover={{ background: 'transparent' }}
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="6"
                        height="24"
                        viewBox="0 0 6 24"
                        fill="none"
                      >
                        <circle cx="3" cy="18" r="2" transform="rotate(-90 3 18)" style={{ fill: iconColor }} />
                        <circle cx="3" cy="12" r="2" transform="rotate(-90 3 12)" style={{ fill: iconColor }} />
                        <circle cx="3" cy="6" r="2" transform="rotate(-90 3 6)" style={{ fill: iconColor }} />
                      </svg>
                    }
                  />
                </MenuButton>
                <MenuList
                  borderRadius="20px"
                  border="1px solid #DCDCDC"
                  boxShadow="0px 8px 24px rgba(0,0,0,0.15)"
                  p="0"
                  zIndex={1502}
                  overflow="hidden"
                  bg="white"
                  motionProps={{
                    variants: {
                      enter: { opacity: 1, scale: 1 },
                      exit: {
                        opacity: 0,
                        scale: 1,
                      },
                    },
                  }}
                >
                  {isArchivedView ? (
                    <MenuItem
                      onClick={() => {
                        onReactivateCaregiver?.(data);
                        setActiveActionRowId(null);
                      }}
                      fontSize="18px"
                      h="52px"
                    >
                      {t('reactivate')}
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem
                        onClick={() => {
                          onEditCaregiver?.(data);
                          setActiveActionRowId(null);
                        }}
                        fontSize="18px"
                        h="52px"
                        borderBottom="1px solid #DCDCDC"
                      >
                        {t('edit')}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          onArchiveCaregiver?.(data);
                          setActiveActionRowId(null);
                        }}
                        fontSize="18px"
                        h="52px"
                        color="invalid.500"
                      >
                        {t('archive')}
                      </MenuItem>
                    </>
                  )}
                </MenuList>
              </Menu>
            </Flex>
          );
        },
      });
    }

    return cols;
  };

  const columns = getColumns();

  return (
    <AleaTable<CaregiverRow & Record<string, unknown>>
      data={
        caregivers.map((c) => ({
          ...c,
          carehome_id: c.carehome?.id,
        })) as (CaregiverRow & Record<string, unknown>)[]
      }
      columns={columns}
      isLoading={isLoading}
      total={total}
      noRecordsText={t('general.noResults')}
      usePagination={usePagination}
      currentPage={currentPage}
      perPage={perPage}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
      activeRowId={activeActionRowId}
      lastColumnColSpan={1}
    />
  );
};

export default CaregiversTable;
